import type { SQLImport as UIAPISQLImport, SqlImportsImportAttributes } from '@customerio/hydra-ui-api';
import { formatFrequency } from '@/utils/time';
import type { SqlDatabaseType, SqlImportDatabase } from '../sql-import-database/entity';

/**
 * A SQL import (the `/integrations/sql-sync` connectors): a saved query against
 * a customer's database, run on an interval to sync people or objects into the
 * workspace. Backed by `/v1/environments/{env}/sql_imports` on the ui_api
 * service (`services/ui_api/sql_imports/`).
 *
 * Imports are listed one database type at a time — `?database=` is required —
 * and every list, get and write reply sideloads the import's database, which
 * `populate.ts` writes into the `sql-import-database` cache. Runs are a
 * separate read (`../sql-import-row`).
 *
 * Timestamps stay epoch seconds, as on the wire: `start_at` uses `0` for "not
 * scheduled", and the connections overview already reads these as numbers.
 */
export type SqlImport = UIAPISQLImport;

/**
 * An import joined to its database, as the list read returns it. The list
 * response carries both arrays, so the join is resolved once there rather than
 * per consumer; `database` is null when the sideload does not carry the
 * referenced database.
 */
export type SqlImportWithDatabase = SqlImport & { database: SqlImportDatabase | null };

/** What an import writes: people, people plus a segment, or objects of one type. */
export type ImportType = NonNullable<SqlImportsImportAttributes['import_type']>;

export const ImportType = {
  workspace: 'workspace',
  workspaceAndSegment: 'workspace_and_segment',
  object: 'object',
} as const satisfies Record<string, ImportType>;

/** Whether a saved import is still a draft or has been activated. */
export type ImportStatus = NonNullable<SqlImportsImportAttributes['import_status']>;

export const ImportStatus = {
  draft: 'draft',
  published: 'published',
} as const satisfies Record<string, ImportStatus>;

export const sqlImportKeys = {
  all: (workspaceId: number) => ['workspace', workspaceId, 'sql_import'] as const,
  lists: (workspaceId: number) => [...sqlImportKeys.all(workspaceId), 'list'] as const,
  list: (workspaceId: number, database: SqlDatabaseType) => [...sqlImportKeys.lists(workspaceId), database] as const,
  details: (workspaceId: number) => [...sqlImportKeys.all(workspaceId), 'detail'] as const,
  detail: (workspaceId: number, id: string) => [...sqlImportKeys.details(workspaceId), id] as const,
};

export function sqlImportIsImportingToSegment(sqlImport: Pick<SqlImport, 'import_type'>): boolean {
  return sqlImport.import_type === ImportType.workspaceAndSegment;
}

/** A first run is scheduled for later; `start_at` is `0` when the import starts on activation. */
export function sqlImportIsScheduled(sqlImport: Pick<SqlImport, 'start_at'>, nowInSeconds: number): boolean {
  return sqlImport.start_at !== 0 && sqlImport.start_at > nowInSeconds;
}

/**
 * The sync interval as copy — "1 hour", "90 minutes", "2 days" — in the largest
 * unit that divides it evenly. Intervals are whole minutes, so stray seconds
 * are dropped first.
 */
export function sqlImportDuration(sqlImport: Pick<SqlImport, 'interval'>): string {
  return formatFrequency(Math.floor(sqlImport.interval / 60) * 60);
}

export const SyncTarget = {
  people: 'People',
  objects: 'Objects',
} as const;

/**
 * Where an import writes to, for display: people, or the name of its object
 * type — "Objects" while that name is not known.
 */
export function sqlImportSyncTarget(
  sqlImport: Pick<SqlImport, 'object_type_id'>,
  objectTypeName: string | null | undefined,
): string {
  if (sqlImport.object_type_id == null) {
    return SyncTarget.people;
  }
  return objectTypeName ?? SyncTarget.objects;
}
